import Image from 'next/image';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { type EoAppPathnames, Link } from '@/i18n/navigation';

import {
  buildGenericCardImageSrc,
  CARD_LOGICAL_SIZE,
} from './buildGenericCardImageSrc';

interface GenericCardProps {
  href: string;
  imageId: string;
  imageAlt: string;
  preTitle?: string;
  title: string;
  subTitle?: string;
  className?: string;
  contentWrapperClassName?: string;
  square?: boolean;
  imagesPath?: '/images' | '/related-images';
  titleLevel?: 'div' | 'h3';
  /**
   * DPR multiplier for the Directus source. Pass 2 for retina-sharp cards
   * (the source is otherwise capped at the logical size and upscaled on 2x
   * displays). Defaults to 1 to keep existing callers unchanged.
   */
  sourceDpr?: number;
  /** Directus-side compression quality. Defaults to the homepage-card value. */
  quality?: number;
}
export const GenericCard: React.FC<GenericCardProps> = ({
  className,
  href,
  imageId,
  imageAlt,
  title,
  imagesPath = '/images',
  subTitle,
  contentWrapperClassName,
  square = false,
  preTitle,
  titleLevel = 'div',
  sourceDpr,
  quality,
}) => {
  const size = React.useMemo(() => {
    return square ? CARD_LOGICAL_SIZE.square : CARD_LOGICAL_SIZE.portrait;
  }, [square]);
  const TitleTag = titleLevel;
  return (
    <Link
      // TODO
      href={href as EoAppPathnames}
      className={twMerge(
        'relative block hover:scale-[0.97] hover:rounded-md transition-all',
        className,
      )}
    >
      <div
        className={twMerge(
          'relative',
          square ? 'aspect-[1/1]' : 'aspect-[4/5]',
        )}
      >
        <Image
          src={buildGenericCardImageSrc({
            imagesPath,
            imageId,
            size,
            sourceDpr,
            quality,
          })}
          alt={imageAlt}
          fill
          sizes={`${size}px`}
          // quality={1}
          // sizes={`
          //   (max-width: 640px) 100vw,  // For small screens, image takes up full width of viewport
          //   (max-width: 768px) 50vw,   // For medium screens, image takes up half width of viewport
          //   (max-width: 1024px) 33vw,  // For large screens, image takes up one-third width of viewport
          //   25vw                       // Default, image takes up one-quarter width of viewport
          // `}
          className="object-cover"
        />
      </div>
      <div
        className={twMerge(
          'absolute bottom-0 right-0 max-w-[90%] bg-slate-50/95 p-3 ',
          square ? '' : 'min-h-[121px]',
          contentWrapperClassName,
        )}
      >
        {/* `text-base` et non `text-sm` : ce n'est pas une étiquette mais du
            CONTENU — le sur-titre de la carte, qu'on lit. À 14 px il était la
            seule chose de la carte sous les 16 px, et sur un téléphone il se
            lisait comme une note de bas de page à côté d'un titre de 18 px. Les
            étiquettes de la page (CONTACT, LIVRAISON…) restent à 14 px, elles :
            leur petite taille EST la hiérarchie. */}
        {preTitle && (
          <div className="text-base leading-tight text-gray-500">
            {preTitle}
          </div>
        )}
        <TitleTag className="line-clamp-2 text-lg !leading-[1.2]">
          {title}
        </TitleTag>
        <div
          className={twMerge(
            'my-2 h-0.5 w-20 bg-primary-900',
            subTitle ? '' : 'mb-0',
          )}
        />
        {subTitle && (
          <div className="line-clamp-2 text-base leading-tight text-gray-500">
            {subTitle}
          </div>
        )}
      </div>
    </Link>
  );
};
